import React from "react";
import { money } from "utils/helpers";
import { Spinner } from "components/DataDisplay";
import { getPerUnitLabel, getProductNoun } from "../helpers";
import MileWarranty from "assets/img/mile_warranty.svg";
import FreeAssistance from "assets/img/free_assistance.svg";
import FreeProtection from "assets/img/free_protection.svg";
import FuelEfficient from "assets/img/fuel_efficient.svg";
import FlatTire from "assets/img/flat_tire.svg";

const BENEFIT_ICONS = {
  mile_warranty: MileWarranty,
  free_assistance: FreeAssistance,
  free_protection: FreeProtection,
  fuel_efficient: FuelEfficient,
  flat_tire: FlatTire,
};

const getTreadTypeAndCondition = (rec) => {
  const treadType = rec.specifications?.tread_type;
  const roadCondition = rec.specifications?.road_condition;

  if (treadType && roadCondition) {
    return `${treadType} ${roadCondition}`;
  }

  return rec.tire_type || rec.season || rec.category;
};

const TireRow = ({ rec, quantity, onApply, isFirst, productType }) => {
  const perTire = parseFloat(rec.price || 0);
  const totalForQty = perTire * quantity;
  const perUnitLabel = getPerUnitLabel(
    rec.productType || rec.product_type || productType
  );
  const noun = getProductNoun(rec.productType || rec.product_type || productType);

  const recSpec = rec.specifications?.size_desc || rec.size_desc;
  const recType = getTreadTypeAndCondition(rec);

  const benefits = rec.additional_benefits || {};
  const hasBenefits = Object.keys(benefits).length > 0;

  return (
    <li
      className={
        "change-tire__row" + (isFirst ? "" : " change-tire__row--divided")
      }
    >
      {rec.image && (
        <img src={rec.image} alt="" className="change-tire__row-img" />
      )}
      <div className="change-tire__row-body">
        <div className="change-tire__row-name">
          {rec.brand} {rec.model}
        </div>
        {(recSpec || recType) && (
          <div className="change-tire__row-spec">
            {recSpec && <span>{recSpec}</span>}
            {recSpec && recType && (
              <span className="change-tire__row-spec-dot" />
            )}
            {recType && <span>{recType}</span>}
          </div>
        )}
        {hasBenefits && (
          <div className="change-tire__row-benefits">
            {Object.entries(benefits).map(([key, label]) => {
              const IconSrc = BENEFIT_ICONS[key];
              return (
                label && (
                  <div key={key} className="change-tire__benefit">
                    {IconSrc && (
                      <img
                        src={IconSrc}
                        alt=""
                        className="change-tire__benefit-icon"
                      />
                    )}
                    <span className="change-tire__benefit-label">{label}</span>
                  </div>
                )
              );
            })}
          </div>
        )}
        <div className="change-tire__row-pricing">
          <div className="change-tire__row-price">${money(totalForQty)}</div>
          <div className="change-tire__row-qty-label">Price for {quantity} {noun.plural}</div>
        </div>
        <div className="change-tire__row-per">${money(perTire)} {perUnitLabel}</div>
        <button
          type="button"
          className="change-tire__select-btn"
          onClick={() => onApply(rec, quantity)}
        >
          Select
        </button>
      </div>
    </li>
  );
};

const getSizeWithoutRating = (sizeDesc) => {
  if (!sizeDesc) return '';
  return sizeDesc.split(' ')[0];
};

const ChangeTireSelector = ({ item, changeFlow, onCancel, onApply, onRetry }) => {
  const list = (changeFlow && changeFlow.recommendations) || [];
  const loading = !!(changeFlow && changeFlow.loading);
  const error = changeFlow && changeFlow.error;
  const quantity =
    (changeFlow && changeFlow.quantity) || item.quantity || 4;
  const spec = item.specifications && item.specifications.size_desc;
  const noun = getProductNoun(item.productType);
  const displaySize = getSizeWithoutRating(spec);

  return (
    <div className="change-tire">
      <div className="change-tire__header">
        <div className="change-tire__header-text">
          <div className="change-tire__title">
            Select a replacement {noun.singular}
            {displaySize && ` ${displaySize}`}
          </div>
        </div>
        <button
          type="button"
          className="change-tire__close"
          onClick={onCancel}
          aria-label={`Keep original ${noun.singular}`}
        >
          ×
        </button>
      </div>
      <div className="change-tire__body">
        {loading && list.length === 0 && (
          <div className="change-tire__loading">
            <Spinner />
          </div>
        )}
        {!loading && error && (
          <div className="change-tire__error">
            <span>Could not load replacement options. Please try again.</span>
            {onRetry && (
              <button
                type="button"
                className="change-tire__retry-btn"
                onClick={() => onRetry(item.id)}
              >
                Retry
              </button>
            )}
          </div>
        )}
        {!loading && !error && list.length === 0 && (
          <div className="change-tire__empty">
            No replacement options available for this item.
          </div>
        )}
        <ul className="change-tire__list">
          {list.map((rec, idx) => (
            <TireRow
              key={rec.variant_id || rec.model}
              rec={rec}
              quantity={quantity}
              onApply={onApply}
              isFirst={idx === 0}
              productType={item.productType}
            />
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ChangeTireSelector;
